import { useState } from "react";
import { Link } from "react-router-dom";

const FORM_URL = "https://readdy.ai/api/form/d9nio6kit35ii3jdlrs0";

const faqs = [
  {
    q: "午马影院的剧集资源是免费的吗？",
    a: "是的，午马影院所有剧集资源均免费在线观看。我们致力于为用户提供免费、高清、流畅的追剧体验，无需会员注册即可畅享所有内容。",
  },
  {
    q: "剧集画质如何？支持哪些分辨率？",
    a: "午马影院最高支持1080P高清画质播放，同时兼容720P、480P等多种分辨率选择。系统会根据您的网络状况自动调整最佳画质，确保流畅不缓冲。",
  },
  {
    q: "剧集更新频率是怎样的？",
    a: "我们与各大平台保持同步更新，热播剧集上线后24小时内即可在午马影院观看。每日实时监测剧集更新动态，确保您不错过任何精彩内容。",
  },
  {
    q: "需要注册账号才能观看吗？",
    a: "不需要！午马影院支持免注册观看，打开即看。如果您希望收藏剧集或订阅更新提醒，也可以选择注册账号享受更多个性化服务。",
  },
  {
    q: "支持哪些设备观看？",
    a: "午马影院完美适配电脑、平板和手机等各类设备。无论在办公室、家中还是出行途中，都能随时随享受追剧乐趣。",
  },
  {
    q: "如何联系客服反馈问题？",
    a: "您可以通过页面底部的联系表单提交反馈，或发送邮件至我们的客服邮箱。我们会在24小时内回复您的咨询。",
  },
];

const testimonials = [
  {
    name: "追剧达人小王",
    text: "午马影院真的太方便了！不用注册就能看，画质清晰还不卡顿。追《狂飙》的时候全靠它，更新速度超快，强烈推荐！",
    rating: 5,
  },
  {
    name: "剧迷小李",
    text: "资源很全，想看的剧基本都能找到。分类也很清晰，悬疑、古装、都市一目了然。手机上看也很流畅，已经成为我的追剧必备了。",
    rating: 5,
  },
  {
    name: "资深观众老张",
    text: "用了好几个观影平台，午马影院的体验是最好的。界面简洁没有乱七八糟的广告，剧集更新及时，1080P高清看着特别舒服。",
    rating: 5,
  },
  {
    name: "夜班族小陈",
    text: "上夜班的时候最喜欢偷偷追剧，午马影院免注册这点太贴心了。加载速度很快，不会卡在缓冲页面，好评！",
    rating: 4,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试";
        setFormError(msg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  };

  return (
    <main className="min-h-screen pt-20">
      <div className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20atmosphere%20with%20soft%20golden%20light%20rays%20streaming%20through%20theater%20curtains%2C%20elegant%20minimalist%20stage%20setting%2C%20warm%20amber%20and%20coral%20tones%2C%20premium%20entertainment%20brand%20background%2C%20abstract%20artistic%20composition%20with%20gentle%20bokeh%20effect%2C%20luxurious%20cinema%20aesthetic&width=1600&height=900&seq=about-hero&orientation=landscape"
          alt="关于午马影院"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-6 md:pb-8">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs md:text-sm text-background-50/70 mb-2 flex-wrap">
              <Link to="/" className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <span className="text-background-50 whitespace-nowrap">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50">
              关于午马影院
            </h1>
          </div>
        </div>
      </div>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                品牌故事
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                午马影院创立于2023年，致力于打造华语地区最优质的在线电视剧观看平台。我们深知每一位追剧爱好者的需求——流畅的高清画质、及时的剧集更新、海量的内容资源，以及对用户体验的极致追求。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                从创立之初，午马影院就秉持"追剧不等待，精彩每一刻"的理念，凭借先进的技术架构和精准的内容运营，迅速赢得了广大观众的喜爱与信赖。我们不断优化播放体验，确保每一帧画面都清晰流畅。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                未来，午马影院将继续拓展内容资源，提升服务质量，让更多观众能够随时随地享受到高品质的观剧体验。我们相信，好的内容值得被更多人看到。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 md:p-6 bg-background-100 rounded-xl text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-display mb-1">
                  1000+
                </div>
                <p className="text-xs md:text-sm text-foreground-600">海量剧集资源</p>
              </div>
              <div className="p-5 md:p-6 bg-background-100 rounded-xl text-center">
                <div className="text-3xl md:text-4xl font-bold text-accent-500 font-display mb-1">
                  50万+
                </div>
                <p className="text-xs md:text-sm text-foreground-600">日活跃用户</p>
              </div>
              <div className="p-5 md:p-6 bg-background-100 rounded-xl text-center">
                <div className="text-3xl md:text-4xl font-bold text-secondary-500 font-display mb-1">
                  99.9%
                </div>
                <p className="text-xs md:text-sm text-foreground-600">服务可用率</p>
              </div>
              <div className="p-5 md:p-6 bg-background-100 rounded-xl text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-400 font-display mb-1">
                  24h
                </div>
                <p className="text-xs md:text-sm text-foreground-600">实时同步更新</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-2">
            用户口碑
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
            来自真实用户的评价，是我们前进的动力
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {testimonials.map((t, idx) => (
              <div key={idx} className="p-5 md:p-6 bg-background-50 rounded-xl">
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < t.rating
                          ? "ri-star-fill text-accent-500"
                          : "ri-star-line text-background-300"
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "{t.text}"
                </p>
                <p className="text-xs font-medium text-foreground-900">
                  — {t.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1400px] mx-auto max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-2">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
            有任何问题或建议？我们期待听到您的声音
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="p-5 bg-background-100 rounded-xl text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">
                电子邮箱
              </h3>
              <p className="text-xs text-foreground-500">contact@wumacinema.com</p>
            </div>
            <div className="p-5 bg-background-100 rounded-xl text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-wechat-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">
                微信公众号
              </h3>
              <p className="text-xs text-foreground-500">午马影院官方</p>
            </div>
            <div className="p-5 bg-background-100 rounded-xl text-center">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-time-line text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">
                响应时间
              </h3>
              <p className="text-xs text-foreground-500">24小时内回复</p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4 bg-background-100 p-6 md:p-8 rounded-xl"
            data-readdy-form
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  您的姓名
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入姓名"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  电子邮箱
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入邮箱"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                留言主题
              </label>
              <input
                type="text"
                name="subject"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                placeholder="请输入主题"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                留言内容
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                placeholder="请输入您的留言内容（500字以内）"
              ></textarea>
            </div>
            <input
              type="text"
              name="phone_alt"
              className="honeypot-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <button
              type="submit"
              className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              提交留言
            </button>

            {formStatus === "success" && (
              <p className="text-sm text-center text-primary-600 font-medium">
                留言已成功提交，我们会尽快回复您！
              </p>
            )}
            {formStatus === "error" && (
              <p className="text-sm text-center text-red-500 font-medium">
                {formError}
              </p>
            )}
          </form>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-[1400px] mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-2">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
            快速了解午马影院的使用方式
          </p>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                    {faq.q}
                  </span>
                  <i
                    className={`text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === idx ? "ri-subtract-line rotate-180" : "ri-add-line"
                    }`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}